import React, { useState } from 'react';
import { Table, Button, Popover, List, Typography, Collapse, Tag, Space } from 'antd';
import moment from 'moment-timezone';

const { Text } = Typography;
const { Panel } = Collapse;

const EventHistoryTable = ({ eventHistory, venueTimeZone, loading, error }) => {
  if (!eventHistory || eventHistory.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '16px' }}>
        <Text type="secondary">No event history found</Text>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '16px' }}>
        <Text type="secondary">Event History Error</Text>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '16px' }}>
        <Text type="secondary">Loading...</Text>
      </div>
    );
  }

  // State for expandable rows
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  // Format the date with the venue's timezone
  const formatDate = date => {
    if (!date) return 'N/A';
    return moment(date)
      .tz(venueTimeZone)
      .format('MM/DD/YYYY hh:mm:ss A');
  };

  // Get admin full name
  const getAdminName = admin => {
    if (!admin) return 'Unknown';
    return `${admin.firstName} ${admin.lastName}`;
  };

  // Column definitions
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      fixed: 'left'
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      fixed: 'left',
      render: date => formatDate(date),
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
    },
    {
      title: 'Admin',
      key: 'admin',
      width: 150,
      render: (_, record) => getAdminName(record.admin)
    },
    {
      title: 'Admin Email',
      key: 'adminEmail',
      width: 220,
      render: (_, record) => record.admin?.email || 'N/A'
    },
    {
      title: 'Changes',
      key: 'changes',
      render: (_, record) => {
        if (!record.changes || record.changes.length === 0) {
          return <Text type="secondary">No changes</Text>;
        }

        // If there are just a few changes, show them directly
        if (record.changes.length <= 3) {
          return <List size="small" dataSource={record.changes} renderItem={item => <List.Item style={{ padding: '4px 0' }}>{item}</List.Item>} />;
        }

        // If there are many changes, show a summary and make them expandable
        return (
          <Popover
            content={
              <List
                size="small"
                style={{ maxHeight: '400px', overflow: 'auto', width: '500px' }}
                dataSource={record.changes}
                renderItem={item => <List.Item>{item}</List.Item>}
              />
            }
            title="All Changes"
            trigger="click"
            placement="right">
            <Button type="link">{record.changes.length} changes - Click to view</Button>
          </Popover>
        );
      }
    },
    {
      title: 'Event Details',
      key: 'details',
      render: (_, record) => {
        const oldName = record.oldValues?.name;
        const newName = record.input?.name;
        const hasNameChange = oldName && newName && oldName !== newName;

        return (
          <Space direction="vertical" size="small">
            <div>
              <Text strong>Event ID: </Text>
              <Text>{record.eventId}</Text>
            </div>

            {hasNameChange && (
              <div>
                <Text strong>Name Change: </Text>
                <Text delete type="secondary">
                  {oldName}
                </Text>
                <Text> → </Text>
                <Tag color="green">{newName}</Tag>
              </div>
            )}

            <Button
              type="link"
              onClick={e => {
                e.stopPropagation();
                toggleExpand(record.id);
              }}>
              {expandedRowKeys.includes(record.id) ? 'Hide Details' : 'Show More Details'}
            </Button>
          </Space>
        );
      }
    }
  ];

  // Toggle row expansion
  const toggleExpand = recordId => {
    setExpandedRowKeys(prevKeys => (prevKeys.includes(recordId) ? prevKeys.filter(key => key !== recordId) : [...prevKeys, recordId]));
  };

  // Define expandable row content
  const expandedRowRender = record => {
    const { oldValues, input } = record;

    // Group changes by category for better organization
    const categorizeChanges = changes => {
      const categories = {
        General: [],
        'Dates & Times': [],
        'Add-ons': [],
        Stalls: [],
        RV: [],
        Questions: [],
        Other: []
      };

      changes.forEach(change => {
        if (change.includes('name') || change.includes('Description') || change.includes('map') || change.includes('agreement')) {
          categories['General'].push(change);
        } else if (change.includes('date') || change.includes('time')) {
          categories['Dates & Times'].push(change);
        } else if (change.includes('Add-on')) {
          categories['Add-ons'].push(change);
        } else if (change.includes('Stall')) {
          categories['Stalls'].push(change);
        } else if (change.includes('RV')) {
          categories['RV'].push(change);
        } else if (change.includes('question')) {
          categories['Questions'].push(change);
        } else {
          categories['Other'].push(change);
        }
      });

      // Remove empty categories
      // eslint-disable-next-line no-unused-vars
      return Object.entries(categories).filter(([_, items]) => items.length > 0);
    };

    const categorizedChanges = categorizeChanges(record.changes || []);

    return (
      <div style={{ padding: '16px 32px', backgroundColor: '#f5f5f5' }}>
        <Collapse defaultActiveKey={['1']} bordered={false}>
          <Panel header="Changes by Category" key="1">
            {categorizedChanges.map(([category, items]) => (
              <div key={category} style={{ marginBottom: '16px' }}>
                <Typography.Title level={5}>{category}</Typography.Title>
                <List
                  size="small"
                  dataSource={items}
                  renderItem={item => (
                    <List.Item style={{ padding: '4px 0' }}>
                      <Text>{item}</Text>
                    </List.Item>
                  )}
                />
              </div>
            ))}
          </Panel>

          <Panel header="Before & After Comparison" key="2">
            <div style={{ display: 'flex' }}>
              <div style={{ flex: 1, marginRight: '16px' }}>
                <Typography.Title level={5}>Before</Typography.Title>
                <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px' }}>{JSON.stringify(oldValues, null, 2)}</pre>
              </div>
              <div style={{ flex: 1 }}>
                <Typography.Title level={5}>After</Typography.Title>
                <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px' }}>{JSON.stringify(input, null, 2)}</pre>
              </div>
            </div>
          </Panel>
        </Collapse>
      </div>
    );
  };

  return (
    <Table
      dataSource={eventHistory}
      columns={columns}
      rowKey="id"
      expandable={{
        expandedRowRender,
        expandedRowKeys,
        onExpand: (expanded, record) => {
          if (expanded) {
            setExpandedRowKeys([...expandedRowKeys, record.id]);
          } else {
            setExpandedRowKeys(expandedRowKeys.filter(key => key !== record.id));
          }
        }
      }}
      pagination={{
        position: ['bottomCenter'],
        pageSize: 10,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50']
      }}
      scroll={{ x: 'max-content' }}
      bordered
    />
  );
};

export default EventHistoryTable;
